import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableSkeleton } from '../Skeleton';

export interface Coach {
  id: string;
  name: string;
  email: string;
  image?: string;
  status: 'Active' | 'Pending Activation';
  areaOfExpertise: string;
  completedSessions: number;
  lastSession: string;
}

interface CoachesTableProps {
  coaches: Coach[];
  onAddCoach: () => void;
  onViewProfile: (coach: Coach) => void;
  onUpdateTarget: (coach: Coach) => void;
  onMessage: (coach: Coach) => void;
  onSearch: (query: string) => void;
  loading?: boolean;
}

const CoachesTable: React.FC<CoachesTableProps> = ({
  coaches,
  onAddCoach,
  onViewProfile,
  onUpdateTarget,
  onMessage,
  onSearch,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleRowClick = (coachId: string) => {
    navigate(`/coaches/${coachId}`);
  };

  const toggleMenu = (e: React.MouseEvent, coachId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === coachId ? null : coachId);
  };

  const handleMenuClick = (e: React.MouseEvent, callback: () => void) => {
    e.stopPropagation();
    callback();
    setOpenMenuId(null);
  };

  console.log("coaches",coaches)

  return (
    <div className="">
      {/* Search and Add Button */}
     

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl border border-[#F2F4F7] p-4 min-h-[600px] shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
         <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative w-64">
          <input
            type="text"
            placeholder="Search Coaches"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-4 text-sm text-gray-600 placeholder-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-0"
          />
          <svg
            className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          onClick={onAddCoach}
          className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Add Coach
        </button>
      </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Coach</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Status</th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Area of Expertise
              </th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Completed Sessions
              </th>
              <th className="px-5 py-3 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">Last Session</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6">
                  <TableSkeleton rows={8} columns={7} />
                </td>
              </tr>
            ) : coaches.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-gray-500">
                  No coaches found
                </td>
              </tr>
            ) : (
              coaches.map((coach) => (
              <tr 
                key={coach.id} 
                className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    {coach.image ? (
                      <img 
                        src={coach.image} 
                        alt={coach.name}
                        className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-semibold text-gray-900">
                          {coach.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => handleRowClick(coach.id)}
                      className="font-medium text-gray-900 whitespace-nowrap text-[14px] overflow-hidden text-ellipsis max-w-[200px] hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                    >
                      {coach.name}
                    </button>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-full px-2 py-1.5 text-[11px] font-medium whitespace-nowrap ${
                      coach.status === 'Active'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-orange-50 text-orange-600'
                    }`}
                  >
                    {coach.status}
                  </span>
                </td>
                <td className="px-5 py-4 text-sm text-gray-900 max-w-[250px] truncate">{coach.areaOfExpertise}</td>
                <td className="px-5 py-4 text-sm text-gray-900">{coach.completedSessions}</td>
                <td className="px-5 py-4 text-sm text-gray-900">{coach.lastSession}</td>
                <td className="px-5 py-4">
                  <div className="relative">
                    <button 
                      className="rounded p-1 hover:bg-gray-100 transition-colors"
                      onClick={(e) => toggleMenu(e, coach.id)}
                    >
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </button>
                    {openMenuId === coach.id && (
                      <div className="absolute right-0 top-full z-10 mt-1 whitespace-nowrap rounded-lg border border-gray-100 bg-white py-1 shadow-lg min-w-[140px]">
                        <button
                          onClick={(e) =>
                            handleMenuClick(e, () => onViewProfile(coach))
                          }
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={(e) =>
                            handleMenuClick(e, () => onUpdateTarget(coach))
                          }
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Update Target
                        </button>
                        <button
                          onClick={(e) =>
                            handleMenuClick(e, () => onMessage(coach))
                          }
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Message
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CoachesTable;
